import {
  ActionProvider as ActionProviderV2,
  AppMenuContextProvider,
  Icon,
  SidebarAppLayout,
  ThemeProvider as ThemeProviderV2,
  ToastProvider as ToastProviderV2
} from "@introist/react-foundation/v2";
import { BrowserRouter as Router, Route, Routes, Outlet } from "react-router-dom";

import { Identified, useIdentity } from "IdentityProvider";
import { RpcProvider } from "services/rpc/RpcProvider";

import {
  NewAppRoute,
  getPublicRoutes,
  getPrivateRoutes,
  RootRoute,
  appRoutes
} from "../../AppRoutes";

import { AppMenu } from "routes/App/AppMenu";
import { MultiCommander, MultiCommanderAnnouncementModal } from "components/organisms";
import { useFeatureFlag } from "../../hooks";
import { useLocalStorage } from "usehooks-ts";
import { logout } from "../../services/firebase";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { OrganisationLogo } from "../../components/atoms";
import { useWorkspaces } from "./AppMenu/hooks/useWorkspaces";
import compact from "lodash.compact";
import { SupportDrawer } from "./AppMenu/components/SupportDrawer";
import { useState } from "react";
import FeatherIcon from "feather-icons-react";
import { Activity, Award } from "lucide-react";

export const AppArea = ({ fullscreen }: { fullscreen?: boolean }) => {
  const { user } = useIdentity();
  const { workspaces, createWorkspace, selectWorkspace } = useWorkspaces();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const hasDataStudio = useFeatureFlag("data-studio").isEnabled;
  const hasAiStudio = useFeatureFlag("ai-studio").isEnabled;
  const hasWellbeingDashboard = useFeatureFlag("wellbeing-dashboard").isEnabled;
  const hasPerformanceReviews = useFeatureFlag("performance-reviews").isEnabled;

  const [activeProduct, setActiveProduct] = useLocalStorage("intr.am.product", "automation-studio");
  const [supportDrawerOpen, setSupportDrawerOpen] = useState(false);

  if (fullscreen) {
    return <Outlet />;
  }

  const productItems =
    hasDataStudio || hasAiStudio || hasWellbeingDashboard || hasPerformanceReviews
      ? compact([
          hasDataStudio
            ? {
                key: "data-studio",
                title: "Data Studio",
                icon: <FeatherIcon icon="database" size={16} />,
                active: activeProduct === "data-studio",
                onClick: () => {
                  setActiveProduct("data-studio");
                }
              }
            : null,
          {
            key: "automation-studio",
            title: "Automation Studio",
            icon: <Icon name="trigger" />,
            active: activeProduct === "automation-studio",
            onClick: () => {
              setActiveProduct("automation-studio");
              navigate("/");
            }
          },
          hasWellbeingDashboard
            ? {
                key: "wellbeing",
                title: "Well-being",
                icon: <Activity size={16} />,
                active: activeProduct === "wellbeing",
                onClick: () => {
                  setActiveProduct("wellbeing");
                  navigate("/wellbeing");
                }
              }
            : null,
          hasPerformanceReviews
            ? {
                key: "performance-reviews",
                title: "Performance Reviews",
                icon: <Award size={16} />,
                active: activeProduct === "performance-reviews",
                onClick: () => {
                  setActiveProduct("performance-reviews");
                  navigate("/performance-reviews");
                }
              }
            : null,
          hasAiStudio
            ? {
                key: "ai-studio",
                title: "AI Studio",
                icon: <Icon name="robot" />,
                active: activeProduct === "ai-studio",
                onClick: () => {
                  setActiveProduct("ai-studio");
                  navigate("/aistudio");
                }
              }
            : null,
          {
            key: "support",
            title: "Support",
            icon: <Icon name="bulb" />,
            onClick: () => setSupportDrawerOpen(true),
            bottom: true
          }
        ])
      : undefined;

  const workspace = {
    user,
    organisation: {
      name: user.workspace?.name ?? "",
      logo: <OrganisationLogo workspace={user?.workspace} />
    },
    workspaces,
    onLogout: () => {
      logout()
        .then(() => queryClient.invalidateQueries())
        .then(() => navigate(appRoutes.login.path));
    },
    createWorkspace: (name: string) => createWorkspace && createWorkspace({ name }),
    switchWorkspace: selectWorkspace
  };

  return (
    <SidebarAppLayout
      appMenu={
        <AppMenu
          productKey={activeProduct}
          productTitle={(productItems ?? []).find(i => i.key === activeProduct)?.title}
          workspace={workspace}
          hasProducts={!!productItems}
        />
      }
      breakpoint="50px"
      productItems={productItems}
      workspace={workspace}
    >
      <Outlet /> {/* This Outlet is key for nested routing */}
      <MultiCommander />
      <MultiCommanderAnnouncementModal />
      <SupportDrawer open={supportDrawerOpen} onClose={() => setSupportDrawerOpen(false)} />
    </SidebarAppLayout>
  );
};

const PrivateRoutes = ({ fullscreen }: { fullscreen?: boolean }) => {
  return (
    <Identified>
      <AppMenuContextProvider localStorageKey="intr.am.c">
        <AppArea fullscreen={fullscreen} />
      </AppMenuContextProvider>
    </Identified>
  );
};

const renderRoutes = (routes: NewAppRoute[]): React.ReactElement[] => {
  return routes.map(route => (
    <Route key={route.key} path={route.path} element={route.element}>
      {route.children && renderRoutes(route.children)}
    </Route>
  ));
};

export const Application = () => {
  const privateRoutes = getPrivateRoutes();
  const publicRoutes = getPublicRoutes();
  const privateFullscreenRoutes = privateRoutes.filter(route => route.fullscreen);
  const privateSidebarRoutes = privateRoutes.filter(route => !route.fullscreen);

  return (
    <RpcProvider>
      <ThemeProviderV2>
        <ToastProviderV2 defaultPlacement="bottom-center">
          <ActionProviderV2 defaultErrorMessage="Something failed!">
            <Router>
              <Routes>
                {/* PRIVATE FULLSCREEN ROUTES */}
                <Route element={<PrivateRoutes fullscreen />}>
                  {renderRoutes(privateFullscreenRoutes)}
                </Route>
                {/* PRIVATE ROUTES */}
                <Route element={<PrivateRoutes />}>
                  <Route index element={<RootRoute />} />
                  {renderRoutes(privateSidebarRoutes)}
                </Route>
                {/* PUBLIC ROUTES */}
                {renderRoutes(publicRoutes)}
              </Routes>
            </Router>
          </ActionProviderV2>
        </ToastProviderV2>
      </ThemeProviderV2>
    </RpcProvider>
  );
};
